/**
 * LabourTab Component
 * Labour rates and calculations for installation
 */

import React, { useMemo } from 'react';
import { Card, Button, EditableCell } from '../ui/FormElements';
import { formatCurrency, calculateGirth, getGirthBand, calculateTotalArea } from '../../utils/calculations';
import { DEFAULT_LABOUR_RATES } from '../../constants/flatCalc';

const LabourTab = ({ areas, spec, labour, onChange, onNext }) => {
  const totalArea = calculateTotalArea(areas.areas);

  const getRate = (id, fallback) => {
    return labour.find(l => l.id === id)?.rate ?? fallback;
  };

  const setRate = (id, rate) => {
    const existing = labour.find(l => l.id === id);
    if (existing) {
      onChange(labour.map(l => l.id === id ? { ...l, rate } : l));
    } else {
      onChange([...labour, { id, rate }]);
    }
  };

  const isMechFix = spec.membraneFixingMethod === 'mech_fix';
  const membraneRate = isMechFix 
    ? DEFAULT_LABOUR_RATES.membrane_mech 
    : DEFAULT_LABOUR_RATES.membrane_adhered;

  const labourItems = useMemo(() => {
    const items = [];
    
    // Check if membrane is selected
    const hasMembrane = spec.membraneType === 'single_ply' 
      ? spec.deckMembraneId 
      : (spec.deckUnderlayId && spec.deckCapId);
    
    // Membrane installation
    if (hasMembrane) {
      items.push({
        id: 'membrane',
        name: `Membrane - ${isMechFix ? 'Mech Fixed' : 'Adhered'}`,
        qty: totalArea,
        unit: 'm²',
        rate: getRate('membrane', membraneRate)
      });
    }
    
    // VCL installation
    if (spec.vclId) {
      items.push({
        id: 'vcl',
        name: 'VCL Installation',
        qty: totalArea,
        unit: 'm²',
        rate: getRate('vcl', DEFAULT_LABOUR_RATES.vcl)
      });
    }
    
    // Insulation installation
    if (spec.insulationId) {
      const insulationRate = spec.insulationLayers === 2 
        ? DEFAULT_LABOUR_RATES.insulation_double 
        : DEFAULT_LABOUR_RATES.insulation_single;
      
      items.push({
        id: 'insulation',
        name: `Insulation - ${spec.insulationLayers || 1} Layer(s)`,
        qty: totalArea,
        unit: 'm²',
        rate: getRate('insulation', insulationRate)
      });
    }
    
    // Upstands
    areas.upstands.forEach(u => {
      const girth = calculateGirth(u.type, u.height, u.width);
      const band = getGirthBand(girth > 0 ? girth : 300);
      
      items.push({
        id: `upstand_${u.id}`,
        name: `${u.location || 'Upstand'} (${band.label})`,
        qty: u.length || 0,
        unit: 'lm',
        rate: getRate(`upstand_${u.id}`, band.rate)
      });
    });
    
    // Detail items
    if (areas.details.outlets > 0) {
      items.push({
        id: 'outlets',
        name: 'Outlets',
        qty: areas.details.outlets,
        unit: 'nr',
        rate: getRate('outlets', DEFAULT_LABOUR_RATES.outlet)
      });
    }
    
    if (areas.details.penetrationsSmall > 0) {
      items.push({
        id: 'penetrations_small',
        name: 'Penetrations (Small)',
        qty: areas.details.penetrationsSmall,
        unit: 'nr',
        rate: getRate('penetrations_small', DEFAULT_LABOUR_RATES.penetration_small)
      });
    }
    
    if (areas.details.penetrationsLarge > 0) {
      items.push({
        id: 'penetrations_large',
        name: 'Penetrations (Large)',
        qty: areas.details.penetrationsLarge,
        unit: 'nr',
        rate: getRate('penetrations_large', DEFAULT_LABOUR_RATES.penetration_large)
      });
    }
    
    if (areas.details.rooflights > 0) {
      items.push({
        id: 'rooflights',
        name: 'Rooflights',
        qty: areas.details.rooflights,
        unit: 'nr',
        rate: getRate('rooflights', DEFAULT_LABOUR_RATES.rooflight)
      });
    }
    
    return items;
  }, [spec, areas, totalArea, labour, isMechFix, membraneRate]);

  const labourTotal = labourItems.reduce((sum, item) => sum + item.qty * item.rate, 0);

  return (
    <div>
      <Card title="Labour Rates">
        {labourItems.length === 0 ? (
          <p className="text-slate-500 text-center py-4">
            Add areas and select products in Spec tab.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-400 border-b border-slate-700">
                  <th className="text-left py-2 px-2">Item</th>
                  <th className="text-right py-2 px-2">Qty</th>
                  <th className="text-center py-2 px-2">Unit</th>
                  <th className="text-right py-2 px-2">Rate</th>
                  <th className="text-right py-2 px-2">Total</th>
                </tr>
              </thead>
              <tbody>
                {labourItems.map(item => (
                  <tr key={item.id} className="border-b border-slate-700/50">
                    <td className="py-2 px-2 text-white">{item.name}</td>
                    <td className="py-2 px-2 text-right text-slate-300">
                      {item.qty.toFixed(2)}
                    </td>
                    <td className="py-2 px-2 text-center text-slate-400">{item.unit}</td>
                    <td className="py-1 px-2 bg-amber-500/10">
                      <EditableCell
                        value={item.rate}
                        onChange={(v) => setRate(item.id, v)}
                        prefix="£"
                        highlight
                      />
                    </td>
                    <td className="py-2 px-2 text-right text-amber-500 font-semibold">
                      {formatCurrency(item.qty * item.rate)}
                    </td>
                  </tr>
                ))}
                <tr className="bg-slate-700/30 font-semibold">
                  <td colSpan="4" className="py-2 px-2 text-white">Labour Total</td>
                  <td className="py-2 px-2 text-right text-amber-500">
                    {formatCurrency(labourTotal)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </Card>
      
      {/* Navigation */}
      <div className="flex justify-end mt-6">
        <Button onClick={onNext}>Next: Summary →</Button>
      </div>
    </div>
  );
};

export default LabourTab;
